import { type FC, lazy } from 'react'
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom'

import { wrapCreateBrowserRouter } from '@sentry/react'

import Result from '@peoplevine/sdk/components/result'
import { AuthProvider } from '@peoplevine/sdk/providers'

import {
  errorBoundary,
  guestPolicy,
  membersPolicy,
  menuPagePolicy,
  pathAliases,
  plusPlanPolicy,
  routeEntries,
  routeObject,
} from './decorators'
import { drawerRoutes, guestRoutes, memberRoutes } from './routes'

const Unauthorized = lazy(() => import('src/layouts/Unauthorized'))
const Authorized = lazy(() => import('src/layouts/Authorized'))
const Drawer = lazy(() => import('src/layouts/Drawer'))
const DefaultLayout = lazy(() => import('src/layouts/Default'))

function createRoutesFromConfig(): RouteObject[] {
  return [
    {
      Component: Unauthorized,
      children: routeEntries(guestRoutes).flatMap(pathAliases).map(routeObject),
    },
    {
      Component: Authorized,
      secured: true,
      children: [
        {
          Component: DefaultLayout,
          children: routeEntries(memberRoutes)
            .map((it) => ({ ...it, membersOnly: true, secured: true }))
            .flatMap(pathAliases)
            .map(plusPlanPolicy)
            .map(membersPolicy)
            .map(menuPagePolicy)
            .map(guestPolicy)
            .map(errorBoundary)
            .map(routeObject),
        },
        {
          Component: Drawer,
          children: routeEntries(drawerRoutes)
            .map((it) => ({ ...it, membersOnly: true, secured: true }))
            .flatMap(pathAliases)
            .map(membersPolicy)
            .map(guestPolicy)
            .map(errorBoundary)
            .map(routeObject),
        },
      ],
    },
    {
      path: '*',
      Component: Result.NotFound,
    },
  ].map(guestPolicy)
}
const RootRoute: FC = () => {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

const createRouter = wrapCreateBrowserRouter(createBrowserRouter)

const routes = [{ element: <RootRoute />, children: createRoutesFromConfig() }]

const router = createRouter(routes)

const RoutesProvider = () => <RouterProvider router={router} />

export default RoutesProvider
